import Icon from "../../../components/AppIcon";
import { formatCurrency } from "../../../utils/formatCurrency";
import type { Payment } from "../../../store/api/paymentsApi";
import { formatDateTime } from "../../../utils/formatDate";

interface PaymentStatsProps {
	payments: Payment[];
}

const PaymentStats: React.FC<PaymentStatsProps> = ({ payments }) => {
	const calculateStats = () => {
		const stats = {
			totalAmount: 0,
			paidAmount: 0,
			pendingAmount: 0,
			overdueAmount: 0,
			paidCount: 0,
			pendingCount: 0,
			overdueCount: 0,
		};

		payments.forEach((payment) => {
			// Safely parse amount, default to 0 if invalid
			const amount = parseFloat(String(payment.amount)) || 0;

			stats.totalAmount += amount;

			if (payment.status === "paid") {
				stats.paidAmount += amount;
				stats.paidCount += 1;
			} else if (payment.status === "pending") {
				const isOverdue =
					payment.due_date && new Date(payment.due_date) < new Date();
				if (isOverdue) {
					stats.overdueAmount += amount;
					stats.overdueCount += 1;
				} else {
					stats.pendingAmount += amount;
					stats.pendingCount += 1;
				}
			} else if (payment.status === "overdue") {
				stats.overdueAmount += amount;
				stats.overdueCount += 1;
			}
		});

		return stats;
	};

	const stats = calculateStats();


	const StatCard = ({
		icon,
		title,
		amount,
		count,
		color,
		bgColor,
	}: {
		icon: string;
		title: string;
		amount: number;
		count: number;
		color: "success" | "warning" | "error" | "primary";
		bgColor: string;
	}) => (
		<div className={`p-3 rounded-lg ${bgColor} overflow-hidden`}>
			<div className="flex items-center space-x-2 mb-2 gap-2">
				<div
					className={`p-1.5 rounded flex-shrink-0 ${
						color === "success"
							? "bg-success/10"
							: color === "warning"
							? "bg-warning/10"
							: color === "error"
							? "bg-error/10"
							: "bg-primary/10"
					}`}
				>
					<Icon
						name={icon as any}
						size={14}
						className={
							color === "success"
								? "text-success"
								: color === "warning"
								? "text-warning"
								: color === "error"
								? "text-error"
								: "text-primary"
						}
					/>
				</div>
				<h4 className="text-xs font-medium text-foreground truncate min-w-0 flex-1">{title}</h4>
			</div>

			<div className="min-w-0">
				<p className="text-base sm:text-lg font-bold text-foreground truncate">
					{formatCurrency(amount, { showAbbreviation: true })}
				</p>
				<p className="text-xs text-muted-foreground truncate">
					{count} payment{count !== 1 ? "s" : ""}
				</p>
			</div>
		</div>
	);

	return (
		<div className="bg-card border border-border rounded-lg card-shadow">
			<div className="p-4 border-b border-border">
				<h3 className="font-semibold text-foreground">Payment Statistics</h3>
				<p className="text-sm text-muted-foreground mt-1">
					Current period overview
				</p>
			</div>

			<div className="p-4 space-y-3">
				{/* Total Revenue */}
				<StatCard
					icon="Currency"
					title="Total Revenue"
					amount={stats.totalAmount}
					count={payments.length}
					color="primary"
					bgColor="bg-primary/5"
				/>

				{/* Collected Payments */}
				<StatCard
					icon="CheckCircle"
					title="Collected"
					amount={stats.paidAmount}
					count={stats.paidCount}
					color="success"
					bgColor="bg-success/5"
				/>

				{/* Pending Payments */}
				<StatCard
					icon="Clock"
					title="Pending"
					amount={stats.pendingAmount}
					count={stats.pendingCount}
					color="warning"
					bgColor="bg-warning/5"
				/>

				{/* Overdue Payments */}
				<StatCard
					icon="AlertTriangle"
					title="Overdue"
					amount={stats.overdueAmount}
					count={stats.overdueCount}
					color="error"
					bgColor="bg-error/5"
				/>
			</div>

			{/* Collection Rate */}
			{payments.length > 0 && stats.totalAmount > 0 && (
				<div className="p-4 border-t border-border">
					<div className="flex items-center justify-between mb-2">
						<span className="text-sm font-medium text-foreground">
							Collection Rate
						</span>
						<span className="text-sm font-bold text-foreground">
							{(((stats.paidAmount || 0) / (stats.totalAmount || 1)) * 100).toFixed(1)}%
						</span>
					</div>

					<div className="w-full bg-muted rounded-full h-2">
						<div
							className="bg-success h-2 rounded-full smooth-transition"
							style={{
								width: `${((stats.paidAmount || 0) / (stats.totalAmount || 1)) * 100}%`,
							}}
						/>
					</div>

					<div className="flex justify-between text-xs text-muted-foreground mt-2">
						<span>Collected: {formatCurrency(stats.paidAmount, { showAbbreviation: true })}</span>
						<span>Total: {formatCurrency(stats.totalAmount, { showAbbreviation: true })}</span>
					</div>
				</div>
			)}

			{/* Upcoming Due Dates */}
			<div className="p-4 border-t border-border">
				<h4 className="text-sm font-medium text-foreground mb-3">
					Upcoming Due Dates
				</h4>

				<div className="space-y-2">
					{payments
						.filter(
							(p) =>
								p.status === "pending" &&
								p.due_date &&
								new Date(p.due_date) >= new Date(),
						)
						.sort(
							(a, b) =>
								new Date(a.due_date!).getTime() -
								new Date(b.due_date!).getTime(),
						)
						.slice(0, 3)
						.map((payment) => (
							<div
								key={payment.id}
								className="flex items-center justify-between p-2 bg-muted/30 rounded"
							>
								<div>
									<p className="text-xs font-medium text-foreground">
										{payment.tenant_name || "N/A"}
									</p>
									<p className="text-xs text-muted-foreground">
										{formatDateTime(payment.due_date!)}
									</p>
								</div>
								<p className="text-xs font-bold text-foreground">
									{formatCurrency(parseFloat(String(payment.amount)) || 0)}
								</p>
							</div>
						))}

					{payments.filter(
						(p) =>
							p.status === "pending" &&
							p.due_date &&
							new Date(p.due_date) >= new Date(),
					).length === 0 && (
						<p className="text-xs text-muted-foreground text-center py-4">
							No upcoming payments
						</p>
					)}
				</div>
			</div>
		</div>
	);
};

export default PaymentStats;
